import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Input } from './ui/input';
import { loadPlacesLibrary } from '../utils/loadGoogleMaps';

/**
 * Address input backed by Google Places Autocomplete (Places API New).
 *
 * Uses `AutocompleteSuggestion.fetchAutocompleteSuggestions` so we can keep the
 * app's own styled <Input> and render a custom suggestion dropdown (instead of
 * Google's web component, which can't be themed to match the design system).
 *
 * Behaviour:
 *  - Free typing calls `onChange(text)` and clears any previously selected
 *    place_id (the typed text no longer corresponds to a resolved place).
 *  - Selecting a suggestion resolves the place_id + formatted address and calls
 *    `onPlaceSelect({ placeId, address, lat, lng })`.
 *  - If the Maps script/key is unavailable, it silently degrades to a plain
 *    text input (no suggestions), so manual address entry always works.
 */
const DEBOUNCE_MS = 250;

const AddressAutocomplete = ({
  value,
  onChange,
  onPlaceSelect,
  id,
  required,
  placeholder,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const placesLibRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  // Guards against selecting a place and then immediately re-fetching for the
  // address text we just set programmatically.
  const skipNextFetchRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    loadPlacesLibrary()
      .then((places) => {
        if (!cancelled) placesLibRef.current = places;
      })
      .catch(() => {
        // Degrade gracefully to a plain text input.
        placesLibRef.current = null;
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const ensureSessionToken = useCallback(() => {
    const places = placesLibRef.current;
    if (!places) return null;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const fetchSuggestions = useCallback(
    async (input) => {
      const places = placesLibRef.current;
      if (!places || !input || input.trim().length < 3) {
        setSuggestions([]);
        setOpen(false);
        return;
      }
      try {
        const { suggestions: results } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            sessionToken: ensureSessionToken(),
          });
        const predictions = (results || [])
          .map((s) => s.placePrediction)
          .filter(Boolean);
        setSuggestions(predictions);
        setActiveIndex(-1);
        setOpen(predictions.length > 0);
      } catch (err) {
        // Network / quota / restriction errors: keep manual entry working.
        setSuggestions([]);
        setOpen(false);
      }
    },
    [ensureSessionToken]
  );

  const handleInputChange = (e) => {
    const text = e.target.value;
    onChange(text);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(text), DEBOUNCE_MS);
  };

  const handleSelect = useCallback(
    async (prediction) => {
      setOpen(false);
      setSuggestions([]);
      try {
        const place = prediction.toPlace();
        await place.fetchFields({
          fields: ['id', 'formattedAddress', 'location'],
        });
        const address = place.formattedAddress || prediction.text?.text || '';
        skipNextFetchRef.current = true;
        onChange(address);
        onPlaceSelect({
          placeId: place.id,
          address,
          lat: place.location ? place.location.lat() : null,
          lng: place.location ? place.location.lng() : null,
        });
      } catch (err) {
        // Fall back to the prediction's display text if details fetch fails.
        const address = prediction.text?.text || '';
        skipNextFetchRef.current = true;
        onChange(address);
        onPlaceSelect({ placeId: prediction.placeId, address, lat: null, lng: null });
      } finally {
        // A new session begins after a selection is resolved.
        sessionTokenRef.current = null;
      }
    },
    [onChange, onPlaceSelect]
  );

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Close the dropdown on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        required={required}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />

      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-[#E2E8F0] bg-white py-1 shadow-lg"
          role="listbox"
        >
          {suggestions.map((prediction, idx) => {
            const main = prediction.mainText?.text || prediction.text?.text || '';
            const secondary = prediction.secondaryText?.text || '';
            return (
              <li
                key={prediction.placeId || idx}
                role="option"
                aria-selected={idx === activeIndex}
                // mousedown (not click) fires before the input blur, so the
                // selection isn't lost to the outside-click handler.
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(prediction);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`cursor-pointer px-3 py-2 text-sm ${
                  idx === activeIndex ? 'bg-[#F1F5F9]' : 'hover:bg-[#F8FAFC]'
                }`}
              >
                <span className="text-[#030712]">{main}</span>
                {secondary && (
                  <span className="text-[#64748B]"> {secondary}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
